import { useMemo } from 'react';
import type { ProcessType } from 'shared/domain/process/types/model';
import type { SiteModel } from 'shared/domain/site/types/model';
import type { FilterValues } from 'shared/types/analytics';
import type { LabelledEntity } from 'shared/types/commonView';
import { useProcesses } from 'store/process';
import { useProject } from 'store/project';
import { useSites } from 'store/site';
import { useAtLeastManagers } from './hooks';

/** What an inspection chart builds its filters from. */
export interface InspectionChartDependencies {
  /** Saved filters of the chart, or its defaults. */
  filters: FilterValues;
  managers: LabelledEntity[];
  processes: ProcessType[];
  sites: SiteModel[];
  projectId: string;
  timezone: string;
}

/**
 * The dependencies of an inspection chart's filters, computed on render: a
 * new object only when one of them changes. Undefined until the saved
 * filters of the selected project are read.
 */
export function useInspectionChartDependencies(
  savedFilters: FilterValues | undefined
): InspectionChartDependencies | undefined {
  const managers = useAtLeastManagers();
  const { _id: projectId, timezone } = useProject();
  const { processes } = useProcesses();
  const { items: sites } = useSites();

  return useMemo(
    () =>
      savedFilters && projectId && timezone
        ? { filters: savedFilters, managers, processes, sites, projectId, timezone }
        : undefined,
    [savedFilters, managers, processes, sites, projectId, timezone]
  );
}
